import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../../modelPaths"
import { CaveOptions } from "../../../RackTypes"

export class CaveCrossmemberBuilder {
  protected config: CaveOptions
  protected material: MaterialInfo
  protected line: string
  protected foldBackDepth: CaveOptions['foldBackDepth']
  protected isRML: boolean
  protected isRM: boolean
  protected crossmemberMaterial: MaterialInfo

  constructor(config: CaveOptions, material: MaterialInfo) {
    this.config = config
    this.material = material
    this.line = config.line
    this.foldBackDepth = config.foldBackDepth
    this.isRML = config.line === 'RML'
    this.isRM = config.line === 'RM'
    this.crossmemberMaterial = {
      main     : { material: 'mg', color: 'black' },
      hardware : { material: this.config.hardware },
    }
  }

  // MARK: Panel Width Helper
  private getPanelWidth(left: boolean): string {
    return left ? this.config.leftPanelWidth : this.config.rightPanelWidth
  }

  private getShelfCrossmemberWidth(left: boolean): string {
    const panelWidth = this.getPanelWidth(left)
    
    return panelWidth.charAt(0) 
  }

  createBottomFoldingBackCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(`CAVE_${this.line}`, `folding back crossmember ${this.foldBackDepth}`),
      slots     : {},
      anchor    : 'top',
      materials : this.crossmemberMaterial,
    }
  }

  createTopFoldingBackCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(`CAVE_${this.line}`, `folding back crossmember ${this.foldBackDepth}`),
      slots     : {},
      anchor    : 'top',
      materials : this.crossmemberMaterial,
    }
  }

  createBottomElevenInchCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(`CAVE_${this.line}`, '11 inch crossmember'),
      slots     : {},
      anchor    : 'top',
      offset    : { z: 0.5 },
      materials : this.crossmemberMaterial,
    }
  }

  createTopElevenInchCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(`CAVE_${this.line}`, '11 inch crossmember'),
      slots     : {},
      anchor    : 'top',
      offset    : { z: 0.5 },
      materials : this.crossmemberMaterial,
    }
  }

  createBottomFullWCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(`CAVE_${this.line}`, `crossmember ${this.foldBackDepth}`),
      slots     : {},
      anchor    : 'top',
      offset    : { z: -0.25 },
      materials : this.crossmemberMaterial,
    }
  }

  createTopFullWCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(`CAVE_${this.line}`, `crossmember ${this.foldBackDepth}`),
      slots     : {},
      anchor    : 'top',
      materials : this.crossmemberMaterial,
      offset    : { z: -0.25, y: 1 },
    }
  }

  // MARK: Hinge Assemblies
  createBottomHingeAssembly(): PartDefinition {
    return {
      mesh   : getRackModel(`CAVE_${this.line}`, 'hinge assembly'),
      slots  : {},
      anchor : 'pin',
      offset : { y: 0.5 },
    }
  }

  createTopHingeAssembly(): PartDefinition {
    return {
      mesh        : getRackModel(`CAVE_${this.line}`, 'hinge assembly'),
      slots       : {},
      anchor      : 'pin',
      adjustments : [{ type: 'rotate', amount: 180, axis: 'Z' }],
    }
  }


  private getCrossmemberMesh(left: boolean, top: boolean): string {
    const shelfWidth = this.getShelfCrossmemberWidth(left)
    const position = top ? 'top' : 'bottom'

    let mesh = this.isRML
      ? getRackModel(`${this.line}_CROSSMEMBERS`, `crossmember ${shelfWidth}`)
      : getRackModel(`${this.line}_CROSSMEMBERS`, `crossmember bottom ${shelfWidth}`)

    // 70" crossmembers are batched in the Cave files for performance reasons
    if (this.isRM && shelfWidth === '7') {
      mesh = getRackModel(`CAVE_${this.line}`, `crossmember ${position} ${shelfWidth}`)
    }

    return mesh
  }

  private getTopCrossmemberMesh(left: boolean, top: boolean): string {
    const shelfWidth = this.getShelfCrossmemberWidth(left)
    const position = top ? 'top' : 'bottom'

    let mesh = getRackModel(`${this.line}_CROSSMEMBERS`, `crossmember top ${shelfWidth}`)

    // 70" crossmembers are batched in the Cave files for performance reasons
    if (this.isRM && shelfWidth === '7') {
      mesh = getRackModel(`CAVE_${this.line}`, `crossmember ${position} ${shelfWidth}`)
    }

    return mesh
  }

  // MARK: Storage/Shelf
  createBottomStorageCrossmember(left: boolean): PartDefinition {
    return {
      mesh        : this.getCrossmemberMesh(left, false),
      slots       : {},
      anchor      : 'front',
      materials   : this.crossmemberMaterial,
      adjustments : left
        ? [{ type: 'rotate', amount: 90, axis: 'Y' }]
        : [
          { type: 'rotate', amount: 90, axis: 'Y' },
          { type: 'flip', axis: 'X' },
        ],
    }
  }

  createTopStorageCrossmember(left: boolean): PartDefinition {
    const shelfWidth = this.getShelfCrossmemberWidth(left)

    return {
      mesh        : this.getTopCrossmemberMesh(left, true),
      slots       : {},
      anchor      : left ? 'front' : 'back',
      materials   : this.crossmemberMaterial,
      adjustments : this.isRM && shelfWidth === '7' 
        ? [{ type: 'rotate', amount: 90, axis: 'Y' }, { type: 'flip', axis: 'Z' }] 
        : [{ type: 'rotate', amount: 90, axis: 'Y' }],
    }
  }

  createBottomPulleyCrossmember(left: boolean, depthOverride?: string): PartDefinition {
    const depth = depthOverride ?? (left ? this.config.leftIndyDepth : this.config.rightIndyDepth)
    const mesh = this.isRML
      ? getRackModel(`${this.line}_CROSSMEMBERS`, `crossmember ${depth}`)
      : getRackModel(`${this.line}_CROSSMEMBERS`, `crossmember bottom ${depth}`)

    return {
      mesh,
      slots       : {},
      anchor      : 'back',
      adjustments : left ? [{ type: 'flip', axis: 'Z' }] : [{ type: 'rotate', amount: 180, axis: 'Y' }],
      materials   : this.crossmemberMaterial,
    }
  }

  createTopPulleyCrossmember(left: boolean, depthOverride?: string): PartDefinition {
    const depth = depthOverride ?? (left ? this.config.leftIndyDepth : this.config.rightIndyDepth)
    const mesh = this.isRML
      ? getRackModel('RML_CROSSMEMBERS', `crossmember ${depth}`)
      : getRackModel(`${this.line}_CROSSMEMBERS`, `crossmember top ${depth}`)

    return {
      mesh,
      slots       : {},
      anchor      : left ? 'back' : 'front',
      materials   : this.crossmemberMaterial,
      adjustments : left
        ? [{ type: 'rotate', amount: 180, axis: 'Y' }, { type: 'flip', axis: 'X' }]
        : [],
    }
  }

  createRMHalfFootBolt(): PartDefinition {
    return {
      mesh      : getRackModel('CAVE_RM', 'rm half foot bolt'),
      slots     : {},
      materials : this.material,
    }
  }

  // The Half Foot is a stabilized that goes into the crossmember slot of an upright
  createHalfFoot(): PartDefinition {
    return {
      mesh  : getRackModel(`CAVE_${this.line}`, `${this.line} half foot`),
      slots : {
        top_bolt    : this.createRMHalfFootBolt(),
        bottom_bolt : this.createRMHalfFootBolt(),
      },
      materials : this.crossmemberMaterial,
      offset    : { z: 1.25 },
    }
  }
}